import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {Observable} from 'rxjs';
import {Repository} from '@app/@core/repositories/repository';
import {SlotModel} from '@app/@core/domain/slot.model';
import {format} from 'date-fns';
import {environment} from '@env/environment';
import {TimeSlotOverview} from '@app/models/time-slot-overview.model';
import Slot = SlotModel.Slot;

export interface SelectPriceParam {
    courseId: string;
    dates: string[];
    times: string;
    isSpecial: boolean;
    priceRuleId: string;
}

export interface CountHourlyDto {
    count: number;
    courseId: string;
    hour: number;
    paid: boolean;
}

export type HDTask = 'MODIFY' | 'UPDATE_PRICE' | 'ROLL_PRICE';

export interface DeleteResult {
    requested: number;
    deleted: number;
}

@Injectable({
    providedIn: 'root'
})
export class SlotRepository extends Repository<Slot> {

    constructor(http: HttpClient) {
        super(http, 'slot');
    }

    search(courseId: string, from: Date, to: Date) {
        const params = {
            courseId,
            startDate: format(from, 'yyyy-MM-dd'),
            endDate: format(to, 'yyyy-MM-dd'),
        };

        return this.http.get<any>(`${environment.apiUrl}/api/slot/search`, {params});
    }

    fetchTimeSlotOverView(fromDate: Date, toDate: Date, courseList: string[]): Observable<TimeSlotOverview[]> {
        const params = {
            fromDate: format(fromDate, 'yyyy-MM-dd'),
            toDate: format(toDate, 'yyyy-MM-dd'),
            courseList
        };

        return this.http.post<any>(`${environment.apiUrl}/api/slot/overview`, params);
        // return this.http.post<TimeSlotOverview[]>("assets/slotOverView.json");
    }

    selectPrice(param: SelectPriceParam): Observable<Slot[]> {
        console.log(param);
        const params = {
            ...param,
            dates: param.dates.map(date => this.ymdToDmy(date))
        };
        return this.http.post<any>(`${environment.apiUrl}/api/slot/selectPrice`, params);
    }

    addSlot(slots: Slot[]): Observable<Slot[]> {
        return this.http.post<any>(`${environment.apiUrl}/api/slot/list`, slots);
    }

    countHourly(from: Date, to: Date): Observable<CountHourlyDto[]> {
        const params = {
            from: format(from, 'yyyy-MM-dd'),
            to: format(to, 'yyyy-MM-dd'),
        };
        return this.http.get<any[]>(`${environment.apiUrl}/api/slot/countHourly`, {params});
    }

    runHotDealTask(task: HDTask, date?: Date, courseId?: string): Observable<number> {
        const body = {task, date, courseId};
        return this.http.put<any>(`${this.entityUrl}/runHotDealTask`, body).pipe(this.mapPrimitive());
    }

    rollbackHotDeal(slotIds: string[]): Observable<Slot[]> {
        return this.http.put<any>(`${this.entityUrl}/rollbackHotDeal`, slotIds);
    }

    makeFlashDeal(slotIds: string[], price: number): Observable<number> {
        const body = {slotIds, price};
        return this.http.put<any>(`${this.entityUrl}/makeFlashDeal`, body).pipe(this.mapPrimitive());
    }

    rollbackFlashDeal(courseIds: string[]): Observable<number> {
        return this.http.put<any>(`${this.entityUrl}/rollbackFlashDeal`, courseIds).pipe(this.mapPrimitive());
    }

    deleteMultiple(ids: string[]): Observable<DeleteResult> {
        return this.http.request<DeleteResult>('delete', `${this.entityUrl}/deleteMultiple`, {
            body: ids
        });
    }

    private ymdToDmy(dateString) {
        const parts = dateString.split('-');
        return `${parts[2]}-${parts[1]}-${parts[0]}`;
    }

    makeNormalToSpecialPrice(priceRuleId: string, normalSlotIds: string[]): Observable<never>{
        return this.http.put<never>(`${this.entityUrl}/makeNormalToSpecial/${priceRuleId}`, normalSlotIds);
    }

    makeSpecialToNormalPrice(specialSlotIds: string[]): Observable<never> {
        return this.http.put<never>(`${this.entityUrl}/makeSpecialToNormal`, specialSlotIds);
    }
}
